import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { HiOutlineHome, HiOutlineCurrencyDollar, HiOutlineBuildingOffice2 } from 'react-icons/hi2';
import { IoFilterOutline } from 'react-icons/io5';
import { BsStars } from 'react-icons/bs';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';

export default function SearchFilters({ filters, totalResults, pagy, filter_options }) {
  const [localFilters, setLocalFilters] = useState({
    query: filters.query || '',
    care_type: filters.care_type || '',
    room_type: filters.room_type || '',
    price_min: filters.price_min || 0,
    price_max: filters.price_max || 10000,
    amenities: filters.amenities || [],
    availability: filters.availability || false,
    sort: filters.sort || 'recommended',
    page: filters.page || 1
  });

  // Sorting options
  const sortOptions = [
    { id: 'recommended', label: 'Recommended' },
    { id: 'price_asc', label: 'Price: Low to High' },
    { id: 'price_desc', label: 'Price: High to Low' },
    { id: 'rating_desc', label: 'Highest Rated' },
    { id: 'newest', label: 'Newest' }
  ];

  // Track which filters are open
  const [openFilter, setOpenFilter] = useState(null);

  // Handle filter changes and auto-apply
  const handleFilterChange = (key, value) => {
    const newFilters = {
      ...localFilters,
      [key]: value
    };

    setLocalFilters(newFilters);

    // Auto-apply filters after a short delay to allow for multiple quick changes
    setTimeout(() => {
      router.visit('/search', {
        data: newFilters,
        preserveState: true,
        only: ['facilities', 'pagy']
      });
      setOpenFilter(null);
    }, 100);
  };

  // Reset filters
  const resetFilters = () => {
    const resetData = {
      query: localFilters.query, // Keep the search query
      care_type: '',
      room_type: '',
      price_min: 0,
      price_max: 10000,
      amenities: [],
      availability: false,
      sort: 'recommended',
      page: 1
    };

    setLocalFilters(resetData);
    router.visit('/search', {
      data: resetData,
      preserveState: true,
      only: ['facilities', 'pagy']
    });
    setOpenFilter(null);
  };

  // Get active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters.care_type) count++;
    if (localFilters.room_type) count++;
    if (localFilters.price_min > 0 || localFilters.price_max < 10000) count++;
    if (localFilters.amenities.length > 0) count++;
    if (localFilters.availability) count++;
    return count;
  };

  // Format price for display
  const formatPrice = (price) => {
    return `$${price.toLocaleString()}`;
  };

  // Handle pagination
  const handlePageChange = (page) => {
    handleFilterChange('page', page);
  };

  // Use filter options from the backend or fallback to defaults if not provided
  const availableAmenities = filter_options?.amenities;

  // Room types based on the unit room_type and bedrooms in the database
  const roomTypes = filter_options?.room_types;

  // Care types based on the facility care_type enum in the database
  const careTypes = filter_options?.care_types;

  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Results count and sort */}
        <div className="flex justify-between items-center mb-4">
          <div className="text-lg font-medium">
            {totalResults} {totalResults === 1 ? 'Community' : 'Communities'} Found
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Sort by:</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="flex items-center gap-1">
                  {sortOptions.find(option => option.id === localFilters.sort)?.label || 'Recommended'}
                  <svg className="h-4 w-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-0" align="end">
                <div className="flex flex-col">
                  {sortOptions.map(option => (
                    <button
                      key={option.id}
                      className={`px-4 py-2 text-left hover:bg-gray-50 ${localFilters.sort === option.id ? 'bg-blue-50 text-blue-600' : ''}`}
                      onClick={() => handleFilterChange('sort', option.id)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Filter buttons */}
        <div className="flex flex-wrap gap-2">
          {/* Care Type Filter - Primary filter based on facility care_type */}
          <Popover open={openFilter === 'care_type'} onOpenChange={(open) => setOpenFilter(open ? 'care_type' : null)}>
            <PopoverTrigger asChild>
              <Button
                variant={localFilters.care_type ? "default" : "outline"}
                size="sm"
                className="flex items-center gap-1 font-medium"
              >
                <HiOutlineHome className="w-4 h-4" /> Care Type
                {localFilters.care_type && (
                  <Badge variant="blue" className="ml-1 px-1.5 py-0.5 text-xs">
                    1
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-4 shadow-lg rounded-lg" align="start">
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900 border-b pb-2">Care Type</h3>
                <div className="space-y-2">
                  {careTypes.map(type => (
                    <div key={type.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded">
                      <input
                        type="radio"
                        id={`care-${type.id}`}
                        name="care_type"
                        checked={localFilters.care_type === type.id}
                        onChange={() => handleFilterChange('care_type', type.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                      />
                      <Label htmlFor={`care-${type.id}`} className="text-sm cursor-pointer">{type.label}</Label>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end pt-2 border-t mt-2">
                  <Button variant="ghost" size="sm" onClick={() => setOpenFilter(null)}>Close</Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Price Range Filter - Based on monthly_cost_cents in units table */}
          <Popover open={openFilter === 'price'} onOpenChange={(open) => setOpenFilter(open ? 'price' : null)}>
            <PopoverTrigger asChild>
              <Button
                variant={(localFilters.price_min > 0 || localFilters.price_max < 10000) ? "default" : "outline"}
                size="sm"
                className="flex items-center gap-1 font-medium"
              >
                <HiOutlineCurrencyDollar className="w-4 h-4" /> Monthly Cost
                {(localFilters.price_min > 0 || localFilters.price_max < 10000) && (
                  <Badge variant="blue" className="ml-1 px-1.5 py-0.5 text-xs">
                    1
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4 shadow-lg rounded-lg" align="start">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 border-b pb-2">Monthly Cost</h3>

                <div className="space-y-6">
                  <div className="pt-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-500">Min: {formatPrice(localFilters.price_min)}</span>
                      <span className="text-sm text-gray-500">Max: {formatPrice(localFilters.price_max)}</span>
                    </div>
                    <Slider
                      value={[parseInt(localFilters.price_min), parseInt(localFilters.price_max)]}
                      min={0}
                      max={10000}
                      step={100}
                      onValueChange={(value) => {
                        setLocalFilters(prev => ({
                          ...prev,
                          price_min: value[0],
                          price_max: value[1]
                        }));
                      }}
                      onValueCommit={(value) => {
                        handleFilterChange('price_min', value[0]);
                        handleFilterChange('price_max', value[1]);
                      }}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {[3000, 5000, 7000, 9000].map(price => (
                      <button
                        key={price}
                        type="button"
                        onClick={() => {
                          handleFilterChange('price_min', 0);
                          handleFilterChange('price_max', price);
                        }}
                        className={`text-sm border rounded-md py-1.5 px-2 ${parseInt(localFilters.price_max) === price && parseInt(localFilters.price_min) === 0 ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 hover:bg-gray-50'}`}
                      >
                        Under {formatPrice(price)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end pt-2 border-t mt-2">
                  <Button variant="ghost" size="sm" onClick={() => setOpenFilter(null)}>Close</Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Room Type Filter - Based on unit room_type and bedrooms */}
          <Popover open={openFilter === 'room_type'} onOpenChange={(open) => setOpenFilter(open ? 'room_type' : null)}>
            <PopoverTrigger asChild>
              <Button
                variant={localFilters.room_type ? "default" : "outline"}
                size="sm"
                className="flex items-center gap-1 font-medium"
              >
                <HiOutlineBuildingOffice2 className="w-4 h-4" /> Room Type
                {localFilters.room_type && (
                  <Badge variant="blue" className="ml-1 px-1.5 py-0.5 text-xs">
                    1
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-4 shadow-lg rounded-lg" align="start">
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900 border-b pb-2">Room Type</h3>
                <div className="space-y-2">
                  {roomTypes.map(type => (
                    <div key={type.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded">
                      <input
                        type="radio"
                        id={`room-${type.id}`}
                        name="room_type"
                        checked={localFilters.room_type === type.id}
                        onChange={() => handleFilterChange('room_type', type.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                      />
                      <Label htmlFor={`room-${type.id}`} className="text-sm cursor-pointer">{type.label}</Label>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end pt-2 border-t mt-2">
                  <Button variant="ghost" size="sm" onClick={() => setOpenFilter(null)}>Close</Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Amenities Filter - Facility features */}
          <Popover open={openFilter === 'amenities'} onOpenChange={(open) => setOpenFilter(open ? 'amenities' : null)}>
            <PopoverTrigger asChild>
              <Button
                variant={localFilters.amenities.length > 0 ? "default" : "outline"}
                size="sm"
                className="flex items-center gap-1 font-medium"
              >
                <BsStars className="w-4 h-4" /> Amenities
                {localFilters.amenities.length > 0 && (
                  <Badge variant="blue" className="ml-1 px-1.5 py-0.5 text-xs">
                    {localFilters.amenities.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-4 shadow-lg rounded-lg" align="start">
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900 border-b pb-2">Amenities</h3>
                <div className="grid grid-cols-1 gap-1 max-h-64 overflow-y-auto pr-2">
                  {availableAmenities.map(amenity => (
                    <div key={amenity.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded">
                      <Checkbox
                        id={`amenity-${amenity.id}`}
                        checked={localFilters.amenities.includes(amenity.id)}
                        onCheckedChange={(checked) => {
                          const newAmenities = checked
                            ? [...localFilters.amenities, amenity.id]
                            : localFilters.amenities.filter(id => id !== amenity.id);
                          handleFilterChange('amenities', newAmenities);
                        }}
                      />
                      <Label htmlFor={`amenity-${amenity.id}`} className="text-sm cursor-pointer">{amenity.label}</Label>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end pt-2 border-t mt-2">
                  <Button variant="ghost" size="sm" onClick={() => setOpenFilter(null)}>Close</Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Availability Filter - Based on available_count in units table */}
          <Button
            variant={localFilters.availability ? "default" : "outline"}
            size="sm"
            className="flex items-center gap-1 font-medium"
            onClick={() => {
              handleFilterChange('availability', !localFilters.availability);
            }}
          >
            <span>🔑</span> Available Now
            {localFilters.availability && (
              <Badge variant="blue" className="ml-1 px-1.5 py-0.5 text-xs">
                1
              </Badge>
            )}
          </Button>

          {/* Reset Filters */}
          {getActiveFilterCount() > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="flex items-center gap-1"
              onClick={resetFilters}
            >
              <IoFilterOutline className="w-4 h-4" /> Reset All
            </Button>
          )}
        </div>

        {/* Active filters */}
        {getActiveFilterCount() > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {localFilters.care_type && (
              <Badge variant="secondary" className="px-3 py-1 flex items-center gap-1">
                {careTypes.find(t => t.id === localFilters.care_type)?.label || localFilters.care_type}
                <button
                  onClick={() => handleFilterChange('care_type', '')}
                  className="ml-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </Badge>
            )}

            {localFilters.room_type && (
              <Badge variant="secondary" className="px-3 py-1 flex items-center gap-1">
                {roomTypes.find(t => t.id === localFilters.room_type)?.label || localFilters.room_type}
                <button
                  onClick={() => handleFilterChange('room_type', '')}
                  className="ml-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </Badge>
            )}

            {(localFilters.price_min > 0 || localFilters.price_max < 10000) && (
              <Badge variant="secondary" className="px-3 py-1 flex items-center gap-1">
                {localFilters.price_min > 0 && localFilters.price_max < 10000
                  ? `${formatPrice(localFilters.price_min)} - ${formatPrice(localFilters.price_max)}`
                  : localFilters.price_min > 0
                    ? `Min: ${formatPrice(localFilters.price_min)}`
                    : `Max: ${formatPrice(localFilters.price_max)}`
                }
                <button
                  onClick={() => {
                    handleFilterChange('price_min', 0);
                    handleFilterChange('price_max', 10000);
                  }}
                  className="ml-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </Badge>
            )}

            {localFilters.amenities.length > 0 && localFilters.amenities.map(amenityId => (
              <Badge key={amenityId} variant="secondary" className="px-3 py-1 flex items-center gap-1">
                {availableAmenities.find(a => a.id === amenityId)?.label || amenityId}
                <button
                  onClick={() => {
                    handleFilterChange('amenities', localFilters.amenities.filter(id => id !== amenityId));
                  }}
                  className="ml-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </Badge>
            ))}

            {localFilters.availability && (
              <Badge variant="secondary" className="px-3 py-1 flex items-center gap-1">
                Available Now
                <button
                  onClick={() => handleFilterChange('availability', false)}
                  className="ml-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </Badge>
            )}
          </div>
        )}

        {/* Pagination */}
        {pagy && pagy.pages > 1 && (
          <div className="mt-6">
            <Pagination>
              <PaginationContent>
                {pagy.prev && (
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(pagy.prev);
                      }}
                    />
                  </PaginationItem>
                )}

                {Array.from({ length: pagy.pages }, (_, i) => i + 1).map(page => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      href="#"
                      isActive={page === pagy.page}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(page);
                      }}
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ))}

                {pagy.next && (
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(pagy.next);
                      }}
                    />
                  </PaginationItem>
                )}
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
    </div>
  );
}
